import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { SentryService } from '../logger/sentry.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlingService {
  constructor(private sentry: SentryService) {}

  handleError<T>(operation = 'operation') {
    return (error: HttpErrorResponse): Observable<T> => {
      const status = error.status ?? 'unknown status';
      const errorMessage = error.error ?? 'no error message';

      // If a native error is caught, do not transform it. We only want to
      // transform response errors that are not wrapped in an `Error`.
      if (errorMessage instanceof ErrorEvent) {
        throw errorMessage;
      }

      const context = {
        operation,
        status,
        url: error.url || 'unknown',
        errorMessage,
      } as Record<string, any>;

      // console.error only in dev — in production captureConsoleIntegration(['error']) is active,
      // so console.error would duplicate the explicit captureException below
      if (!environment.production) {
        console.error(`HTTP Error in ${operation}`, error, context);
      }

      this.sentry.captureException(error, context);

      // If the error is a 401 Unauthorized, redirect to the login page
      if (status === 401) {
        window.location.href = '/login';
        return new Observable<T>(); // empty observable to avoid further execution
      }

      // If the error is a 404 Not Found, return inside the stream — prevents createErrorHandler
      // from firing a second Sentry event for the same error already captured above
      if (status === 404) {
        return throwError(() => new Error('Resource not found'));
      }

      // Same for 500 — captureException already fired above, don't let the re-throw hit createErrorHandler
      if (status === 500) {
        return throwError(() => new Error('Internal server error'));
      }

      if (status === 400) {
        const message = `${operation} failed: server returned code ${status} with body "${errorMessage}"`;
        const errorObj: any = new Error(message);
        errorObj.status = status;
        errorObj.statusText = error.statusText;
        errorObj.body = errorMessage;
        errorObj.url = error.url || undefined;
        return throwError(() => errorObj);
      }

      // For all other errors — return inside the stream for the same reason
      const message = `server returned code ${status} with body "${errorMessage}"`;
      return throwError(() => new Error(`${operation} failed: ${message}`));
    };
  }
}